/**
 * A compact single-stroke vector font — the kind carved into a wooden sign or
 * a milestone. Each glyph is a set of polyline strokes on a fixed grid, so it
 * turns into geometry (no textures, no font files, no `three/examples` loaders)
 * and works identically in a browser, a headless render and a Node test.
 *
 * Grid: x runs left→right from 0, y is the baseline at 0 up to the cap height
 * (`CAP`). Commas and the tail of a `Q`/`J`/`,` dip below the baseline. Every
 * glyph also carries an `advance` (how far the pen moves on to the next
 * letter), so proportional spacing falls out for free.
 */

export const CAP = 7;

/** A glyph: its polyline strokes (each a flat [x,y,x,y,…] run) and pen advance. */
export interface Glyph {
  strokes: number[][];
  advance: number;
}

// Authoring helper: strokes are written as arrays of [x, y] pairs for
// legibility, then flattened once at module load.
type Pt = [number, number];
function g(advance: number, ...strokes: Pt[][]): Glyph {
  return { advance, strokes: strokes.map((s) => s.flat()) };
}

// A tiny filled dot (period, colon, the foot of ! and :) — a little square
// loop so the stroke-thickener gives it body.
const DOT = (x: number, y: number): Pt[] => [
  [x - 0.25, y],
  [x + 0.25, y],
  [x + 0.25, y + 0.5],
  [x - 0.25, y + 0.5],
  [x - 0.25, y],
];

/**
 * The glyph table — humanist capitals, digits and the punctuation a signpost
 * actually needs. Lowercase is mapped onto capitals at build time, so a mixed-
 * case string still renders (medieval signage is caps anyway).
 */
export const FONT: Record<string, Glyph> = {
  ' ': g(3),
  A: g(6, [[0, 0], [2.75, 7], [5.5, 0]], [[1.15, 2.6], [4.35, 2.6]]),
  B: g(5.6,
    [[0, 0], [0, 7]],
    [[0, 7], [3.5, 7], [4.6, 6.2], [4.6, 4.7], [3.5, 3.9], [0, 3.9]],
    [[0, 3.9], [3.9, 3.9], [5, 3], [5, 0.9], [3.9, 0], [0, 0]]),
  C: g(5.6, [[5.2, 5.4], [4.1, 6.7], [2.2, 7], [0.7, 5.9], [0, 4], [0, 3], [0.7, 1.1], [2.2, 0], [4.1, 0.3], [5.2, 1.6]]),
  D: g(5.8, [[0, 0], [0, 7]], [[0, 7], [2.8, 7], [4.6, 5.7], [5.2, 3.5], [4.6, 1.3], [2.8, 0], [0, 0]]),
  E: g(5.2, [[5, 7], [0, 7], [0, 0], [5, 0]], [[0, 3.5], [3.6, 3.5]]),
  F: g(5, [[5, 7], [0, 7], [0, 0]], [[0, 3.5], [3.4, 3.5]]),
  G: g(6, [[5.2, 5.4], [4.1, 6.7], [2.2, 7], [0.7, 5.9], [0, 4], [0, 3], [0.7, 1.1], [2.2, 0], [4.2, 0.2], [5.2, 1.4], [5.2, 3], [3.2, 3]]),
  H: g(5.6, [[0, 0], [0, 7]], [[5.4, 0], [5.4, 7]], [[0, 3.6], [5.4, 3.6]]),
  I: g(2, [[1, 0], [1, 7]]),
  J: g(4.6, [[4, 7], [4, 1.7], [3.2, 0.3], [1.9, 0], [0.7, 0.7], [0, 2]]),
  K: g(5.4, [[0, 0], [0, 7]], [[5.2, 7], [0, 3.2]], [[1.7, 4.3], [5.4, 0]]),
  L: g(4.8, [[0, 7], [0, 0], [4.8, 0]]),
  M: g(6.6, [[0, 0], [0, 7], [3.3, 2.4], [6.6, 7], [6.6, 0]]),
  N: g(5.8, [[0, 0], [0, 7], [5.6, 0], [5.6, 7]]),
  O: g(6, [[2.3, 7], [0.8, 6.1], [0, 4], [0, 3], [0.8, 0.9], [2.3, 0], [3.3, 0], [4.8, 0.9], [5.6, 3], [5.6, 4], [4.8, 6.1], [3.3, 7], [2.3, 7]]),
  P: g(5.4, [[0, 0], [0, 7]], [[0, 7], [3.7, 7], [4.8, 6], [4.8, 4.5], [3.7, 3.6], [0, 3.6]]),
  Q: g(6,
    [[2.3, 7], [0.8, 6.1], [0, 4], [0, 3], [0.8, 0.9], [2.3, 0], [3.3, 0], [4.8, 0.9], [5.6, 3], [5.6, 4], [4.8, 6.1], [3.3, 7], [2.3, 7]],
    [[3.4, 1.9], [5.8, -0.5]]),
  R: g(5.6,
    [[0, 0], [0, 7]],
    [[0, 7], [3.7, 7], [4.8, 6], [4.8, 4.5], [3.7, 3.6], [0, 3.6]],
    [[2.7, 3.6], [5.4, 0]]),
  S: g(5.2, [[5, 5.6], [3.9, 6.8], [1.7, 7], [0.5, 6], [0.5, 4.7], [1.5, 3.9], [3.7, 3.4], [4.7, 2.6], [4.7, 1], [3.5, 0], [1.2, 0.2], [0.2, 1.4]]),
  T: g(5, [[0, 7], [5, 7]], [[2.5, 7], [2.5, 0]]),
  U: g(5.6, [[0, 7], [0, 2], [0.8, 0.5], [2.6, 0], [4.6, 0.5], [5.4, 2], [5.4, 7]]),
  V: g(5.6, [[0, 7], [2.8, 0], [5.6, 7]]),
  W: g(7.4, [[0, 7], [1.4, 0], [3.7, 5], [6, 0], [7.4, 7]]),
  X: g(5.4, [[0, 0], [5.4, 7]], [[0, 7], [5.4, 0]]),
  Y: g(5.4, [[0, 7], [2.7, 3.5], [5.4, 7]], [[2.7, 3.5], [2.7, 0]]),
  Z: g(5.2, [[0, 7], [5.2, 7], [0, 0], [5.2, 0]]),

  '0': g(5.4, [[2.1, 7], [0.7, 6], [0, 3.5], [0.7, 1], [2.1, 0], [3.3, 0], [4.7, 1], [5.4, 3.5], [4.7, 6], [3.3, 7], [2.1, 7]], [[0.9, 1.3], [4.5, 5.7]]),
  '1': g(4, [[1, 5.4], [2.6, 7], [2.6, 0]], [[0.9, 0], [4.2, 0]]),
  '2': g(5.2, [[0.4, 5.4], [1.1, 6.5], [2.6, 7], [4, 6.7], [4.7, 5.6], [4.6, 4.3], [3.7, 3.1], [0.3, 0], [5, 0]]),
  '3': g(5.2, [[0.5, 6.2], [2, 7], [3.7, 7], [4.7, 6], [4.7, 4.8], [3.6, 3.8], [2.4, 3.8]], [[3.6, 3.8], [4.9, 2.8], [4.9, 1.1], [3.7, 0], [1.8, 0], [0.3, 1]]),
  '4': g(5.2, [[3.7, 0], [3.7, 7], [0, 2.4], [5, 2.4]]),
  '5': g(5.2, [[4.7, 7], [0.9, 7], [0.5, 3.9], [1.6, 4.5], [3.5, 4.5], [4.7, 3.5], [4.7, 1.4], [3.5, 0.1], [1.6, 0.1], [0.4, 1.2]]),
  '6': g(5.2, [[4.5, 6], [3.1, 7], [1.6, 6.6], [0.6, 4.8], [0.3, 2.6], [0.9, 0.8], [2.2, 0], [3.6, 0.3], [4.6, 1.5], [4.6, 2.9], [3.6, 4], [1.9, 4.2], [0.6, 3.2]]),
  '7': g(5.2, [[0.4, 7], [5, 7], [2, 0]]),
  '8': g(5.4,
    [[2.6, 3.8], [1.3, 4.5], [1.3, 5.9], [2.6, 7], [3.9, 7], [5.1, 5.9], [5.1, 4.5], [3.9, 3.8], [2.6, 3.8]],
    [[2.6, 3.8], [1, 3], [0.5, 1.6], [1.6, 0.2], [3.6, 0.2], [4.7, 1.6], [4.2, 3], [2.6, 3.8]]),
  '9': g(5.2, [[0.7, 1], [2.1, 0], [3.6, 0.4], [4.6, 2.2], [4.9, 4.4], [4.3, 6.2], [3, 7], [1.6, 6.7], [0.6, 5.5], [0.6, 4.1], [1.6, 3], [3.3, 2.8], [4.6, 3.8]]),

  '.': g(2, DOT(0.5, 0)),
  ',': g(2, [[0.9, 0.6], [0.9, 0], [0.2, -1]]),
  "'": g(2, [[0.6, 7], [0.6, 5.3]]),
  '"': g(3, [[0.6, 7], [0.6, 5.3]], [[1.9, 7], [1.9, 5.3]]),
  '!': g(2, [[0.5, 7], [0.5, 2]], DOT(0.5, 0)),
  '?': g(4.8, [[0.4, 5.6], [1.1, 6.6], [2.5, 7], [3.7, 6.6], [4.3, 5.5], [4.1, 4.3], [2.4, 3.2], [2.4, 2]], DOT(2.4, 0)),
  '-': g(4, [[0.5, 3.5], [3.5, 3.5]]),
  '&': g(6,
    [[5.6, 0], [2, 3.6], [1, 4.8], [1, 5.9], [1.9, 6.8], [3.1, 6.6], [3.5, 5.5], [3, 4.4], [0.6, 1.6], [1.6, 0.1], [3.2, 0.1], [4.6, 1.4], [5.2, 2.9]]),
  ':': g(2, DOT(0.5, 3.9), DOT(0.5, 0.9)),
  '/': g(4, [[0, -0.5], [3.6, 7.2]]),
  '(': g(2.8, [[2.2, 7.4], [0.7, 5], [0.7, 2], [2.2, -0.4]]),
  ')': g(2.8, [[0.4, 7.4], [1.9, 5], [1.9, 2], [0.4, -0.4]]),
};

/** Fallback for an unknown character: the pen just advances (like a space). */
export const MISSING: Glyph = FONT[' '];
